import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { KeyboardArrowDown } from '@mui/icons-material';
import { AppColors } from '../styles/colors';
import { DeviceType } from '../types/device';
import { useDevice } from '../context/DeviceContext';

const Container = styled.div<{ $device: DeviceType }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: flex-start;
  position: relative;
`;

const DropdownContainer = styled.div<{
  $radius?: string;
  $height?: string;
  $borderColor?: string;
  $focusBorderColor?: string;
  $isFocused?: boolean;
  $background?: string;
}>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: ${({ $height }) => $height || '48px'};
  border-radius: ${({ $radius }) => $radius || '12px'};
  border: ${({ $isFocused, $focusBorderColor, $borderColor }) => {
    if ($isFocused && $focusBorderColor) return `1px solid ${$focusBorderColor}`;
    if ($borderColor) return `1px solid ${$borderColor}`;
    return `1px solid ${AppColors.onInput1}`;
  }};
  background-color: ${({ $background }) => $background || AppColors.input};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-sizing: border-box;
`;

const SelectedValue = styled.div<{
  $padding?: string;
  $placeholderColor?: string;
  $isPlaceholder?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  padding: ${({ $padding }) => $padding || '16px'};
  padding-right: 40px; /* 화살표 아이콘 공간 확보 */
  font-size: 16px;
  line-height: 1;
  color: ${({ $isPlaceholder, $placeholderColor }) => {
    if ($isPlaceholder && $placeholderColor) return $placeholderColor;
    return $isPlaceholder ? AppColors.onInput1 : AppColors.onInput3;
  }};
  user-select: none;
  overflow: hidden;
  box-sizing: border-box;
`;

const ArrowIcon = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${AppColors.onInput2};
  transition: transform 0.2s ease-in-out;
  pointer-events: none;
  
  ${({ $isOpen }) => $isOpen && 'transform: translateY(-50%) rotate(180deg);'}
`;

const DropdownList = styled.div<{
  $isOpen: boolean;
  $radius?: string;
  $maxHeight?: string;
  $openUpward?: boolean;
  $left: number;
  $width: number;
  $top: number;
  $bottom: number;
}>`
  position: fixed;
  top: ${({ $openUpward, $top }) => ($openUpward ? 'auto' : `${$top}px`)};
  bottom: ${({ $openUpward, $bottom }) => ($openUpward ? `${$bottom}px` : 'auto')};
  left: ${({ $left }) => `${$left}px`};
  width: ${({ $width }) => `${$width}px`};
  background-color: ${AppColors.input};
  border: 1px solid ${AppColors.onInput1};
  border-radius: ${({ $radius }) => $radius || '12px'};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1300;
  max-height: ${({ $maxHeight }) => $maxHeight || '200px'};
  overflow-y: auto;
  opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
  visibility: ${({ $isOpen }) => $isOpen ? 'visible' : 'hidden'};
  transform: ${({ $isOpen }) => $isOpen ? 'translateY(0)' : 'translateY(-8px)'};
  transition: all 0.2s ease-in-out;
`;

const DropdownItem = styled.div<{
  $isSelected?: boolean;
  $isHovered?: boolean;
}>`
  padding: 12px 16px;
  background-color: ${({ $isHovered, $isSelected }) => {
    if ($isSelected) return AppColors.onInput1 + '20'; // 20% opacity
    if ($isHovered) return AppColors.onInput1 + '10'; // 10% opacity
    return 'transparent';
  }};
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
  
  &:hover {
    background-color: ${AppColors.onInput1}20;
  }
  
  &:first-child {
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }
  
  &:last-child {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
  }
  
  &:only-child {
    border-radius: 12px;
  }
`;

export interface CustomDropdownOption {
  value: string;
  content: React.ReactNode; // UI를 받을 수 있도록 ReactNode 타입
  disabled?: boolean;
}

export interface CustomDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: CustomDropdownOption[];
  placeholder?: React.ReactNode; // placeholder도 UI를 받을 수 있도록
  maxHeight?: string;
  
  // 스타일링 props
  radius?: string;
  height?: string;
  padding?: string;
  borderColor?: string;
  focusBorderColor?: string;
  placeholderColor?: string;
  background?: string;
}

export const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = "선택해주세요",
  maxHeight,
  
  // 스타일링 props 기본값
  radius = "12px",
  height = "48px",
  padding = "16px",
  borderColor = AppColors.onInput1,
  focusBorderColor = AppColors.onInput1,
  placeholderColor = AppColors.onInput1,
  background = AppColors.input,
}) => {
  const device = useDevice();
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    left: 0,
    width: 0,
    top: 0,
    bottom: 0,
    maxHeight: maxHeight || '200px',
  });
  const containerRef = useRef<HTMLDivElement>(null);
  
  const selectedOption = options.find(option => option.value === value);
  const isPlaceholder = !selectedOption;
  
  // 외부 클릭 감지하여 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getListHeight = () => {
    const parsed = Number.parseInt((maxHeight || '200px').replace('px', ''), 10);
    return Number.isNaN(parsed) ? 200 : parsed;
  };

  const updateOpenDirection = () => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const estimatedListHeight = getListHeight();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const shouldOpenUpward = spaceBelow < estimatedListHeight && spaceAbove > spaceBelow;
    setOpenUpward(shouldOpenUpward);

    const availableHeight = shouldOpenUpward ? Math.max(spaceAbove - 8, 120) : Math.max(spaceBelow - 8, 120);
    const resolvedMaxHeight = `${Math.min(estimatedListHeight, availableHeight)}px`;

    setDropdownPosition({
      left: rect.left,
      width: rect.width,
      top: rect.bottom + 4,
      bottom: window.innerHeight - rect.top + 4,
      maxHeight: resolvedMaxHeight,
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    updateOpenDirection();

    const handleViewportChange = () => {
      updateOpenDirection();
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isOpen, maxHeight]);

  const handleToggle = () => {
    const nextOpen = !isOpen;
    if (nextOpen) {
      updateOpenDirection();
    }
    setIsOpen(nextOpen);
    setIsFocused(nextOpen); // 열릴 때 focused, 닫힐 때 unfocused
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setIsFocused(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      setIsFocused(false);
    }
  };

  return (
    <Container ref={containerRef} $device={device}>
      <DropdownContainer
        $radius={radius}
        $height={height}
        $borderColor={borderColor}
        $focusBorderColor={focusBorderColor}
        $isFocused={isFocused}
        $background={background}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <SelectedValue
          $padding={padding}
          $placeholderColor={placeholderColor}
          $isPlaceholder={isPlaceholder}
        >
          {selectedOption?.content || placeholder}
        </SelectedValue>
        
        <ArrowIcon $isOpen={isOpen}>
          <KeyboardArrowDown />
        </ArrowIcon>
        
        <DropdownList
          $isOpen={isOpen}
          $radius={radius}
          $maxHeight={dropdownPosition.maxHeight}
          $openUpward={openUpward}
          $left={dropdownPosition.left}
          $width={dropdownPosition.width}
          $top={dropdownPosition.top}
          $bottom={dropdownPosition.bottom}
          role="listbox"
        >
          {options.map((option) => (
            <DropdownItem
              key={option.value}
              $isSelected={option.value === value}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(option.value);
              }}
              role="option"
              aria-selected={option.value === value}
            >
              {option.content}
            </DropdownItem>
          ))}
        </DropdownList>
      </DropdownContainer>
    </Container>
  );
};
